import { useMemo, useCallback } from 'react';
import type { PrimaryCountryISO } from '@/constants/countries';
import { Button, Grid, useTheme } from '@simplywallst/ui-core';
import { MinimalDropdown } from '@/components/Dropdown';
import FilterOutline from '@simplywallst/ui-core/icons/FilterOutline';
import { getFlagCharacter } from '@utilities/getFlagCharacter';
import useWindowSize from '@/hooks/useWindowSize';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { Filter } from './styled';

interface MarketOption {
  option: PrimaryCountryISO | string;
  href?: string;
  label: string;
}

type IndustryOption = {
  option: string;
  label: string;
};

interface Props {
  marketOptions: MarketOption[];
  industryOptions: IndustryOption[];
  selectedMarket: string;
  selectedIndustry: string;
  hideIndustryFilter?: boolean;
  onMarketChange: (market: string) => void;
  onIndustryChange: (industry: string) => void;
  onAdvancedFilterClick: () => void;
}

export const MarketIndustryFilter = ({
  marketOptions,
  industryOptions,
  selectedMarket,
  selectedIndustry,
  hideIndustryFilter,
  onMarketChange,
  onIndustryChange,
  onAdvancedFilterClick,
}: Props) => {
  const theme = useTheme();
  const { width: screenWidth = 0 } = useWindowSize();
  const { isDesktop, os } = useUserAgent();
  const isLargeScreen =
    screenWidth === 0 ? isDesktop : screenWidth > theme.mdMinRaw;
  const countryFlag =
    os === 'Windows'
      ? ''
      : getFlagCharacter(selectedMarket as PrimaryCountryISO);
  const marketLabel = useMemo(
    () =>
      selectedMarket === 'global'
        ? '🌎\u00A0 Global'
        : `${countryFlag}\u00A0 ${selectedMarket.toUpperCase()} ${
            isLargeScreen ? 'Market' : ''
          }`,
    [selectedMarket, isLargeScreen]
  );
  const industryLabel = useCallback((): string => {
    if (industryOptions && industryOptions.length > 0) {
      const currentIndustry = industryOptions.find(
        (i) => i.option === selectedIndustry
      );
      if (currentIndustry && currentIndustry.label !== 'Any') {
        return currentIndustry.label;
      }
    }
    return 'Industry';
  }, [selectedIndustry, industryOptions]);
  return (
    <Filter
      justifyContent="space-between"
      templateColumns="auto auto"
      templateRows="40px"
      alignItems="center"
    >
      <Grid templateColumns="auto auto" columnGap="8px">
        <MinimalDropdown
          displayType="inline-block"
          canSearch={true}
          name="basic-filter-market"
          options={marketOptions}
          onSelect={onMarketChange}
          value={selectedMarket}
          autoComplete="off"
          placeholder="Search e.g. United States"
          label={marketLabel}
        />
        {(typeof hideIndustryFilter === 'undefined' || !hideIndustryFilter) && (
          <MinimalDropdown
            displayType="inline-block"
            canSearch={true}
            options={industryOptions}
            onSelect={onIndustryChange}
            value={selectedIndustry}
            name="basic-filter-industry"
            autoComplete="off"
            placeholder="Search e.g. Automobiles"
            label={industryLabel()}
          />
        )}
      </Grid>
      <Button
        styleType="passiveLarge"
        alignIcon="right"
        data-cy-id="advanced-filters-button"
        onClick={onAdvancedFilterClick}
        marginLeft
      >
        Advanced Filters
        <FilterOutline />
      </Button>
    </Filter>
  );
};
